import React, { useMemo, useState, useEffect } from 'react';
import { Table, Tag, Typography, Row, Modal, Space, Tooltip } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { DownloadRounded, DownloadForOfflineRounded} from "@mui/icons-material";
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import TickerChip from "../../components/TickerChip";
import { useTickerSearchIndex } from "../../modules/Search/useTickerSearchIndex";
import { useScreenHeight } from "../../hooks/useScreen";
import ColoredTag from "../../components/Tags/ColoredTag";
import PortfolioExport from "../TradingSignals/PortfolioExport";
import PnL from "../../components/PnL";
import {useSelector} from "react-redux";


const IndexDisplay = ({ index, record})=>{
  const sectorRefill = record?.sectorRefill;
  const sectorAdjusted = record?.sectorWeightAdjusted;
  let color = '#000';
  let string = "";
  if (sectorRefill){
    string = "Ticker Was Included from Sector Refill.\r\n";
    color = 'red';
  }
  if (sectorAdjusted){
      string = "Sector Weight Adjusted.\r\n";
      if (!sectorRefill){
        color = 'orange'
      }
  }
  return (
      <Tooltip title={string}>
        <Typography.Text style={{color}}>
          {index}
        </Typography.Text>
      </Tooltip>
  )
}

const SmaTickersTable = ({ data, etfColors, sectorColors, logoCloudCount=10,
                           hideExport=false, bordered=true }) => {
  const symbols = useMemo(() => data.map(x => x.symbol), [data]);
  const { tickers, loading } = useTickerSearchIndex(symbols);
  const screenHeight = useScreenHeight();
  const isPublic = useSelector(state=>state.public.isPublicAccess)


  const [isExportModalVisible, setIsExportModalVisible] = useState(false); // State for modal visibility


  const hasOriginalWeight = useMemo(() => {
    return data.some(x => x.hasOwnProperty('originalWeight'));
  }, [data]);
  const maxStockWeight = useMemo(() => {
    const stockTickers = data.filter(item => Array.isArray(item?.parents) && item.parents.length > 0);
    return stockTickers.length ? Math.max(...stockTickers.map(item => item['wgt'])) : 0;
  }, [data]);

  const totalWeight = useMemo(() => {
    return data.reduce((sum, item) => sum + (item.wgt || 0), 0);
  }, [data]);

  // Use useMemo to calculate dataWithLogos and ensure it's updated when tickers or data changes
  const dataWithLogos = useMemo(() => {
    return data.sort((a, b) => b.wgt - a.wgt).map((item, index) => {
      const ticker = tickers.find(ticker => ticker.symbol === item.symbol);
      return {
        ...item,
        logo: ticker?.logo || null, // Fallback to null if logo is missing
        name: ticker?.name || 'N/A', // Fallback to 'N/A' if name is missing
        index: index + 1
      };
    });
  }, [tickers, data]);


  const columns = [
    {
      title: '',
      dataIndex: 'index',
      key: 'index',
      render: (text, record) => <IndexDisplay index={text} record={record} />,
      width: 50,
      fixed: 'left'
    },
    {
      title: "Security",
      dataIndex: "name",
      key: "name",
      render: (_, record) => (
        <div>
          <TickerChip ticker={record} />
        </div>
      ),
      fixed: 'left'
    },
    {
      title: 'Weight',
      dataIndex: 'wgt',
      key: 'wgt',
      render: (text) => text && <Typography.Text style={{fontFamily: "Inter. sans-serif", fontSize: 12}}>{(text * 100).toFixed(2)}%</Typography.Text>,
      width: 75,
      sorter: (a, b) => a.wgt - b.wgt,
    },
  ];

  if (hasOriginalWeight) {
    columns.push(...[
      {
        title: 'From (%)',
        dataIndex: 'originalWeight',
        key: 'originalWeight',
        render: (text, record) => {
          const { wgt, originalWeight } = record;
          if (!originalWeight) return '--';
          return <Typography.Text style={{fontFamily: "Inter. sans-serif", fontSize: 12}}>{(text * 100).toFixed(2)}%</Typography.Text>;
        },
        width: 85,
        sorter: (a, b) => a.originalWeight - b.originalWeight,
      },
      {
        title: '',
        dataIndex: 'originalWeight',
        key: 'originalWeight',
        render: (text, record) => {
          const { wgt, originalWeight } = record;
          if (!originalWeight) return '--';
          return <PnL value={wgt-originalWeight} />;
        },
        width: 85,
        sorter: (a, b) => (a.originalWeight - a.wgt) - (b.originalWeight - b.wgt),
      },
    ]);
  }

  columns.push(...[
      {
      title: 'Factor',
      key: 'returnMultiplier',
      render: (_, record) => {
        const {['wgt']: weight} = record;
        if (record?.parents?.length > 0 && maxStockWeight && record.wgt) {
          const multiplier = (maxStockWeight / record.wgt).toFixed(2);
          return <Typography.Text>{multiplier}x</Typography.Text>;
        }
        return '--';
      },
        width: 85
    },
    {
      title: 'Sector',
      dataIndex: 'sector',
      key: 'sector',
      render: sector => (
        <ColoredTag shadow={false} width={150} color={sectorColors[sector]} name={sector} />
      ),
      width: 150
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: industry => (
        <Tag bordered={false}>{industry}</Tag>
      ),
    },
    {
      title: 'ETF Parents',
      dataIndex: 'parents',
      key: 'parents',
      render: parents => (
        <Space style={{maxWidth: 300}} wrap>
          {parents && parents.length > 0 && parents.map((parent) => (
            <ColoredTag shadow={false} width={50} name={parent.toUpperCase()} color={etfColors[parent]} />
          ))}
        </Space>
      ),
    },
  ]);

  const handleExportClick = () => {
    setIsExportModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsExportModalVisible(false);
  };

  return (
    <>
      <Row justify={'end'} className={'mb-3 pr-6'}>
                    {!hideExport && (

                      <ButtonGroup>
                            <Button variant={'text'}
                                    startIcon={<DownloadForOfflineRounded />}
                                    type="primary"
                                    onClick={handleExportClick}
                                    disabled={isPublic}
                            >
                              Export
                            </Button>
                      </ButtonGroup>
                          )}

      </Row>
      <Table
        className={`vertical-align-middle nowrap-headers ${!bordered ? 'borderless' : ''}`}
        columns={columns}
        dataSource={dataWithLogos}
        rowKey="symbol"
        size={'small'}
        pagination={{
          pageSize: 100
        }}
        scroll={{ y: screenHeight - 300, x: 'max-content' }}
        summary={() => (
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={columns.length - 1}>
              <Typography.Text strong>Total Weight</Typography.Text>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <Typography.Text>{(totalWeight * 100).toFixed(2)}%</Typography.Text>
            </Table.Summary.Cell>
          </Table.Summary.Row>
        )}
      />

      {/* Modal for Portfolio Export */}
      <Modal
        title=""
        visible={isExportModalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={1200}
        destroyOnClose={true}
        style={{
          height: '100vh'
        }}
      >
        {/* Passing portfolio data to PortfolioExport */}
        <PortfolioExport portfolio={dataWithLogos.map(item => ({
          symbol: item.symbol,
          wgt: item.wgt
        }))} />
      </Modal>
    </>
  );
};

export default SmaTickersTable;